import { useState, useCallback, useRef } from 'react'
import { useSession } from './chat/useSession'
import { useMessages } from './chat/useMessages'
import { ChatService } from '../services/chatService'

export const useStreamingChat = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const { sessionId, startNewSession } = useSession()
  const { messages, addMessage, updateLastMessage, resetMessages } = useMessages()
  const shouldStopRef = useRef(false)

  const stopAnswering = useCallback(() => {
    shouldStopRef.current = true;
  }, []);

  const sendMessage = useCallback(async (message) => {
    if (isLoading || !message.trim()) return;

    const userMessage = {
      id: Date.now(),
      content: message,
      role: 'user',
      timestamp: new Date().toISOString()
    };

    addMessage(userMessage);
    setIsLoading(true);
    setError(null);
    shouldStopRef.current = false;

    try {
      const response = await ChatService.sendMessage({ message, sessionId, messages });
      
      // Handle rate-limit object returned by handleError
      if (response && response.isRateLimit) {
        const errorMsg = response.message || "You've reached the rate limit. Please wait before sending more messages.";
        setError(errorMsg);
        setIsLoading(false);
        // Add a visible error message in chat
        addMessage({
          id: Date.now() + 1,
          content: errorMsg,
          role: 'assistant',
          isError: true,
          retryAfter: response.retryAfter || 0,
          timestamp: new Date().toISOString()
        });
        return;
      }

      if (!response || !response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const assistantMessage = {
        id: Date.now() + 1,
        content: '',
        role: 'assistant',
        timestamp: new Date().toISOString()
      };

      addMessage(assistantMessage);

      await ChatService.handleStreamingResponse(response, {
        onChunk: (content) => {
          updateLastMessage(content, true);
        },
        onComplete: (finalContent) => {
          updateLastMessage(finalContent, false);
          setIsLoading(false);
        },
        onError: (error) => {
          console.error('Stream error:', error);
          setError(error);
          setIsLoading(false);
          // Remove the failed assistant message
          // (handled internally by updateLastMessage when isTyping=false and empty)
        },
        shouldStop: () => shouldStopRef.current
      });

    } catch (error) {
      console.error('Chat error:', error);
      let errorMessage = 'Failed to send message. Please try again.';
      
      if (error.message.includes('Failed to fetch')) {
        errorMessage = 'Cannot connect to the chat service. Please check your connection.';
      } else if (error.message.includes('HTTP')) {
        errorMessage = `Server error: ${error.message}`;
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Request timed out. Please try again.';
      }
      
      setError(errorMessage);
      setIsLoading(false);
    }
  }, [isLoading, sessionId, addMessage, updateLastMessage]);

  const startNewChat = useCallback(() => {
    const newSessionId = startNewSession()
    resetMessages()
    return newSessionId
  }, [startNewSession, resetMessages])

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    sessionId,
    startNewChat,
    stopAnswering
  }
}